import { useCallback } from "react";
import { useForm } from "@tellescope/forms";
import { useTellescopeMutation, useTellescopeQuery, useUsersQuery } from "@tellescope/tanstack-query";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { type User, UserSearchCombobox } from "../../UserSearchCombobox";

export interface CallHoldQueueSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queueId?: string;
}

export function CallHoldQueueSheet({ open, onOpenChange, queueId }: CallHoldQueueSheetProps) {
  const editMode = !!queueId;
  const { data: queue } = useTellescopeQuery({
    model: "call_hold_queues",
    method: "getOne",
    request: queueId ?? "",
    enabled: !!queueId,
  });

  const { t } = useTranslation("call-queues");

  const createQueueMutation = useTellescopeMutation({
    model: "call_hold_queues",
    method: "createOne",
  });

  const updateQueueMutation = useTellescopeMutation({
    model: "call_hold_queues",
    method: "updateOne",
  });

  const form = useForm({
    fields: {
      title: {
        type: "text",
        initialValue: queue?.title ?? "",
        required: true,
      },
      userIds: {
        type: "multiselect",
        initialValue: queue?.userIds ?? [],
      },
    },
    validateOn: "blur",
  });

  const isPending = createQueueMutation.isPending || updateQueueMutation.isPending;

  const handleSubmit = useCallback(async () => {
    if (editMode && queue) {
      await updateQueueMutation.mutateAsync({
        id: queue.id,
        title: form.values.title,
        userIds: form.values.userIds,
        _options: {
          replaceObjectFields: true,
        },
      });
    } else {
      await createQueueMutation.mutateAsync({
        title: form.values.title,
        userIds: form.values.userIds,
      });
    }
    form.resetForm();
    onOpenChange(false);
  }, [createQueueMutation, editMode, form, onOpenChange, queue, updateQueueMutation]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        form.resetForm();
      }
      onOpenChange(nextOpen);
    },
    [form, onOpenChange]
  );

  const handleUsersChange = useCallback(
    (users: User[]) => {
      form.setFieldValue(
        "userIds",
        users.map(({ id }) => id)
      );
    },
    [form]
  );

  const { data: queriedUsers = [] } = useUsersQuery(form.values.userIds);
  const selectedUsers = form.values.userIds.length ? queriedUsers : [];

  const title = editMode ? t("Edit Queue") : t("Create Queue");
  const description = editMode ? t("Update the call hold queue.") : t("Create a new call hold queue.");
  const submitLabel = editMode ? t("Save") : t("Create");
  const trackingId = editMode ? "call-queues-edit-queue-submit" : "call-queues-create-queue-submit";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        <div className="grid flex-1 auto-rows-min gap-4 px-4 overflow-y-auto overflow-x-hidden w-full">
          <div className="space-y-2">
            <Label htmlFor="queue-title">{t("Title")}</Label>
            <Input id="queue-title" {...form.getFieldProps("title")} placeholder={t("Enter queue title...")} />
          </div>

          <div className="space-y-2">
            <Label>{t("Users")}</Label>
            <UserSearchCombobox
              multiple
              value={selectedUsers}
              onChange={handleUsersChange}
              placeholder={t("Select users...")}
            />
          </div>
        </div>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline">{t("Cancel")}</Button>
          </SheetClose>
          <Button onClick={handleSubmit} disabled={!form.isValid} loading={isPending} data-trackingid={trackingId}>
            {submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
